import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Loader2, TrendingUp, DollarSign, Trophy, Target, Package,
  MapPin, Calendar, Users
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, PieChart, Pie
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { id: 'today', label: 'Hoje' },
  { id: 'week',  label: 'Semana' },
  { id: 'month', label: 'Mês' },
  { id: 'year', label: 'Ano' },
  { id: 'all',  label: 'Tudo' }
];

const STATUS_LABELS = {
  new: 'Novo',
  contacted: 'Contatado',
  negotiating: 'Negociando',
  proposal: 'Proposta',
  won: 'Ganho',
  lost: 'Perdido'
};

const formatBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatBRLShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1000000) return `R$ ${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(1)}K`;
  return formatBRL(n);
};

const getPeriodStart = (period) => {
  const now = new Date();
  switch (period) {
    case 'today': return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    case 'week': {
      const d = new Date(now);
      d.setDate(now.getDate() - 6);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    case 'month': return new Date(now.getFullYear(), now.getMonth(), 1);
    case 'year': return new Date(now.getFullYear(), 0, 1);
    default: return null;
  }
};

const SalesDashboard = ({ showMessage }) => {
  const [period, setPeriod] = useState('month');
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/quotes`)
      ]);
      setLeads(leadsRes.data || []);
      setQuotes(quotesRes.data || []);
    } catch (e) {
      console.error(e);
      showMessage?.('Erro ao carregar painel', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => { load(); }, [load]);

  const periodStart = useMemo(() => getPeriodStart(period), [period]);

  const inPeriod = (iso) => {
    if (!periodStart) return true;
    try { return new Date(iso) >= periodStart; } catch { return false; }
  };

  // Filtered datasets
  const periodLeads = useMemo(() => leads.filter(l => inPeriod(l.createdAt)), [leads, periodStart]); // eslint-disable-line
  const acceptedQuotesPeriod = useMemo(
    () => quotes.filter(q => q.status === 'accepted' && inPeriod(q.updatedAt || q.createdAt)),
    [quotes, periodStart] // eslint-disable-line
  );

  // KPIs
  const totalLeads = periodLeads.length;
  const revenue = acceptedQuotesPeriod.reduce((acc, q) => acc + Number(q.total || 0), 0);
  const wonLeads = periodLeads.filter(l => l.status === 'won').length;
  const closedLeads = periodLeads.filter(l => l.status === 'won' || l.status === 'lost').length;
  const conversionRate = closedLeads > 0 ? Math.round((wonLeads / closedLeads) * 100) : 0;
  const avgTicket = acceptedQuotesPeriod.length > 0 ? revenue / acceptedQuotesPeriod.length : 0;

  // Revenue chart (line by day)
  const revenueByDay = useMemo(() => {
    const start = periodStart || new Date(Date.now() - 30 * 86400000);
    const map = new Map();
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    while (cur <= end) {
      const key = cur.toISOString().slice(0, 10);
      map.set(key, 0);
      cur.setDate(cur.getDate() + 1);
    }
    acceptedQuotesPeriod.forEach(q => {
      try {
        const d = new Date(q.updatedAt || q.createdAt);
        const key = d.toISOString().slice(0, 10);
        if (map.has(key)) map.set(key, map.get(key) + Number(q.total || 0));
      } catch {}
    });
    return Array.from(map.entries()).map(([date, value]) => {
      const d = new Date(date);
      return {
        date: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        receita: value
      };
    });
  }, [acceptedQuotesPeriod, periodStart]);

  // Pipeline by stage (sum of quote totals per lead status)
  const pipeline = useMemo(() => {
    const stages = ['new', 'contacted', 'negotiating', 'proposal', 'won'];
    return stages.map(stage => {
      const stageLeads = leads.filter(l => l.status === stage);
      const stageLeadIds = new Set(stageLeads.map(l => l.id));
      const stageQuotes = quotes.filter(q => q.leadId && stageLeadIds.has(q.leadId));
      const value = stageQuotes.reduce((acc, q) => acc + Number(q.total || 0), 0);
      return { stage: STATUS_LABELS[stage], value, count: stageLeads.length };
    });
  }, [leads, quotes]);

  // Funnel data
  const funnel = useMemo(() => {
    const stages = ['new', 'contacted', 'negotiating', 'proposal', 'won'];
    return stages.map(s => ({
      stage: STATUS_LABELS[s],
      qty: periodLeads.filter(l => l.status === s).length
    }));
  }, [periodLeads]);

  // Top products (from accepted quotes)
  const topProducts = useMemo(() => {
    const map = new Map();
    acceptedQuotesPeriod.forEach(q => {
      (q.items || []).forEach(it => {
        const key = it.productName;
        const prev = map.get(key) || { qty: 0, revenue: 0 };
        map.set(key, {
          qty: prev.qty + Number(it.quantity || 0),
          revenue: prev.revenue + (Number(it.quantity || 0) * Number(it.unitPrice || 0))
        });
      });
    });
    return Array.from(map.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [acceptedQuotesPeriod]);

  // Leads by source
  const sources = useMemo(() => {
    const map = new Map();
    periodLeads.forEach(l => {
      const k = l.source || 'site';
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [periodLeads]);

  const PIE_COLORS = ['#f59e0b', '#ea580c', '#16a34a', '#3b82f6', '#a855f7', '#ec4899'];

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      {/* Header + period filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Painel de Vendas</h2>
          <p className="text-stone-400 text-sm">Métricas e gráficos comerciais — Bagual mode 🔥</p>
        </div>
        <div className="flex gap-1 bg-stone-800 rounded-xl p-1 border border-stone-700">
          {PERIODS.map(p => (
            <button
              key={p.id}
              data-testid={`period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                period === p.id
                  ? 'bg-amber-500 text-stone-900'
                  : 'text-stone-300 hover:bg-stone-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI testId="kpi-revenue" icon={DollarSign} label="Receita" value={formatBRL(revenue)} sub={`${acceptedQuotesPeriod.length} venda(s)`} accent="from-green-500/20 to-green-500/5 border-green-500/30" iconColor="text-green-400" />
        <KPI testId="kpi-leads"   icon={Users}      label="Leads"   value={totalLeads}        sub="no período" accent="from-blue-500/20 to-blue-500/5 border-blue-500/30" iconColor="text-blue-400" />
        <KPI testId="kpi-conversion" icon={Target}  label="Conversão" value={`${conversionRate}%`} sub={`${wonLeads}/${closedLeads} fechados`} accent="from-amber-500/20 to-amber-500/5 border-amber-500/30" iconColor="text-amber-400" />
        <KPI testId="kpi-avg-ticket" icon={Trophy}  label="Ticket médio" value={formatBRLShort(avgTicket)} sub="por venda" accent="from-purple-500/20 to-purple-500/5 border-purple-500/30" iconColor="text-purple-400" />
      </div>

      {/* Revenue chart */}
      <div className="bg-stone-800 border border-stone-700 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-green-400" />
          <h3 className="text-white font-semibold">Receita por dia</h3>
        </div>
        <div style={{ width: '100%', height: 250 }}>
          <ResponsiveContainer>
            <LineChart data={revenueByDay} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid stroke="#44403c" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#a8a29e" fontSize={11} />
              <YAxis stroke="#a8a29e" fontSize={11} tickFormatter={formatBRLShort} />
              <Tooltip contentStyle={{ background: '#1c1917', border: '1px solid #44403c', borderRadius: 8, color: '#fff' }} formatter={(v) => formatBRL(v)} />
              <Line type="monotone" dataKey="receita" stroke="#16a34a" strokeWidth={2.5} dot={{ fill: '#16a34a', r: 3 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pipeline + Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-stone-800 border border-stone-700 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-4 h-4 text-amber-400" />
            <h3 className="text-white font-semibold">Pipeline de Vendas</h3>
          </div>
          <p className="text-stone-500 text-xs mb-3">Valor potencial de orçamentos por estágio do CRM</p>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={pipeline} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid stroke="#44403c" strokeDasharray="3 3" />
                <XAxis dataKey="stage" stroke="#a8a29e" fontSize={11} />
                <YAxis stroke="#a8a29e" fontSize={11} tickFormatter={formatBRLShort} />
                <Tooltip contentStyle={{ background: '#1c1917', border: '1px solid #44403c', borderRadius: 8, color: '#fff' }} formatter={(v) => formatBRL(v)} />
                <Bar dataKey="value" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-stone-800 border border-stone-700 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-blue-400" />
            <h3 className="text-white font-semibold">Funil de Conversão</h3>
          </div>
          <p className="text-stone-500 text-xs mb-3">Quantidade de leads em cada estágio</p>
          <div className="space-y-2">
            {funnel.map((f, idx) => {
              const max = Math.max(...funnel.map(x => x.qty), 1);
              const pct = (f.qty / max) * 100;
              return (
                <div key={f.stage}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-stone-300">{f.stage}</span>
                    <span className="text-white font-bold">{f.qty}</span>
                  </div>
                  <div className="h-7 bg-stone-900 rounded-lg overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-600 transition-all flex items-center px-2"
                      style={{ width: `${pct}%` }}
                    >
                      {pct > 20 && f.qty > 0 && (
                        <span className="text-xs text-stone-900 font-bold">
                          {Math.round((f.qty / (funnel[0]?.qty || 1)) * 100)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top products + Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-stone-800 border border-stone-700 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-4 h-4 text-amber-400" />
            <h3 className="text-white font-semibold">Top Produtos Vendidos</h3>
          </div>
          {topProducts.length === 0 ? (
            <p className="text-stone-500 text-sm py-8 text-center">Sem vendas no período. Marque orçamentos como "Aceito" para gerar dados aqui.</p>
          ) : (
            <div className="space-y-2">
              {topProducts.map((p, idx) => (
                <div key={p.name} className="bg-stone-900 border border-stone-700 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white text-sm font-medium truncate">{p.name}</div>
                      <div className="text-stone-500 text-xs">{p.qty} unid. vendidas</div>
                    </div>
                  </div>
                  <div className="text-green-400 font-bold text-sm flex-shrink-0">{formatBRL(p.revenue)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-stone-800 border border-stone-700 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-purple-400" />
            <h3 className="text-white font-semibold">Leads por Origem</h3>
          </div>
          {sources.length === 0 ? (
            <p className="text-stone-500 text-sm py-8 text-center">Sem leads no período</p>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={sources} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${e.value}`} labelLine={false}>
                    {sources.map((_, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1c1917', border: '1px solid #44403c', borderRadius: 8, color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KPI = ({ testId, icon: Icon, label, value, sub, accent, iconColor }) => (
  <div data-testid={testId} className={`bg-gradient-to-br ${accent} border rounded-xl p-4`}>
    <div className="flex items-start justify-between mb-2">
      <span className="text-stone-300 text-xs font-medium uppercase tracking-wide">{label}</span>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <div className="text-white text-2xl font-bold leading-none mb-1 truncate">{value}</div>
    <div className="text-stone-400 text-xs">{sub}</div>
  </div>
);

export default SalesDashboard;
